import React from 'react';
import NavLink from '../NavLink/navlink-component';
import MediaComponent from '../Media/media-component';

/**
 * FeaturedGuides, magazine layout for the blog/guides section:
 *   • one large featured card on the left (photo fills, dark gradient, copy over it)
 *   • up to two stacked horizontal cards on the right (photo left, copy right)
 * All copy is data-driven from the blog records. Mirrors for RTL.
 */

const readLabel = (isRtl) => (isRtl ? 'דקות קריאה' : 'MIN READ');
const hrefFor = (b) => `/blog/${b.slug}`;
const imgOf = (b) => b.cardImage || b.heroImage || b.image || '';
const excerptOf = (b) => b.excerpt || b.subtitle || '';

function ArrowButton({ isRtl, size = 'md' }) {
    const dim = size === 'sm' ? 'h-11 w-11' : 'h-12 w-12';
    return (
        <span className={`${dim} shrink-0 rounded-full bg-secondary-500 text-primary-950 flex items-center justify-center transition-colors duration-200 group-hover:bg-secondary-600`}>
            <svg viewBox="0 0 24 24" fill="none" className={`h-5 w-5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}

function ReadTime({ blog, isRtl, tone }) {
    if (!blog.readTime) return <span />;
    const cls = tone === 'light' ? 'text-white/70' : 'text-primary-400';
    return (
        <span className={`text-[11px] font-bold uppercase tracking-[0.14em] ${cls}`}>
            {blog.readTime} {readLabel(isRtl)}
        </span>
    );
}

function FeaturedCard({ blog, isRtl }) {
    const img = imgOf(blog);
    const excerpt = excerptOf(blog);
    return (
        <NavLink
            href={hrefFor(blog)}
            title={blog.title}
            preserveStyle
            showUnderline={false}
            dir={isRtl ? 'rtl' : 'ltr'}
            className="group relative flex min-h-[440px] h-full flex-col justify-end overflow-hidden rounded-3xl bg-primary-950"
        >
            {img && (
                <div className="absolute inset-0">
                    <MediaComponent src={img} alt={blog.title} objectFit="cover" quality={70} useSekeleton={false}
                        wrapperClassName="h-full w-full"
                        className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/45 to-primary-950/5" />

            <div className="relative z-10 p-7 md:p-9">
                {blog.category && (
                    <span className="text-[12px] font-extrabold uppercase tracking-[0.16em] text-secondary-500">{blog.category}</span>
                )}
                <h3 className="mt-3 font-secondary text-3xl md:text-4xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
                    {blog.title}
                </h3>
                {excerpt && (
                    <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-white/75 line-clamp-2">{excerpt}</p>
                )}
                <div className="mt-6 flex items-center justify-between gap-4">
                    <ReadTime blog={blog} isRtl={isRtl} tone="light" />
                    <ArrowButton isRtl={isRtl} />
                </div>
            </div>
        </NavLink>
    );
}

export function ListCard({ blog, isRtl }) {
    const img = imgOf(blog);
    const excerpt = excerptOf(blog);
    return (
        <NavLink
            href={hrefFor(blog)}
            title={blog.title}
            preserveStyle
            showUnderline={false}
            dir={isRtl ? 'rtl' : 'ltr'}
            className="group flex h-full overflow-hidden rounded-3xl border border-primary-100 bg-[#FAFAF8] transition-all duration-300 hover:border-secondary-500/40 hover:shadow-[0_16px_40px_rgba(11,34,101,0.12)]"
        >
            {img && (
                <div className="relative w-[38%] shrink-0 overflow-hidden">
                    <MediaComponent src={img} alt={blog.title} objectFit="cover" quality={60} useSekeleton={false}
                        wrapperClassName="absolute inset-0 h-full w-full"
                        className="h-full w-full transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
            )}
            <div className="flex flex-1 flex-col p-5 md:p-6">
                {blog.category && (
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-primary-300">{blog.category}</span>
                )}
                <h3 className="mt-2 font-secondary text-xl md:text-2xl font-extrabold leading-tight tracking-[-0.02em] text-primary-500 transition-colors group-hover:text-secondary-600 line-clamp-2">
                    {blog.title}
                </h3>
                {excerpt && (
                    <p className="mt-2 text-sm leading-relaxed text-primary-300 line-clamp-3">{excerpt}</p>
                )}
                <div className="mt-auto flex items-center gap-4 pt-4">
                    <ReadTime blog={blog} isRtl={isRtl} tone="dark" />
                    <span className="h-px flex-1 bg-primary-100" />
                    <ArrowButton isRtl={isRtl} size="sm" />
                </div>
            </div>
        </NavLink>
    );
}

export default function FeaturedGuides({ blogs = [], lang = 'he' }) {
    const list = (blogs || []).filter(Boolean);
    if (!list.length) return null;
    const isRtl = lang === 'he';
    const [featured, ...rest] = list;
    const side = rest.slice(0, 2);

    return (
        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
            <FeaturedCard blog={featured} isRtl={isRtl} />
            {side.length > 0 && (
                <div className="grid grid-cols-1 gap-6" style={{ gridTemplateRows: side.length > 1 ? '1fr 1fr' : '1fr' }}>
                    {side.map((b) => (
                        <ListCard key={b.id || b.slug} blog={b} isRtl={isRtl} />
                    ))}
                </div>
            )}
        </div>
    );
}
